/**
 * Pipeline run trigger functions.
 * Each function fires the corresponding POST endpoint and returns the
 * resulting run summary. All mutations return HTTP 202.
 */

import { apiPost } from "./client.js";
import type {
  DiscoveryRunSummary,
  ScoreRunSummary,
  ReportRunSummary,
  StatementVersionSummary,
  DocumentRunSummary,
  RecurringRunSummary,
  TemplateRunSummary,
} from "./product-types.js";

// ── Generic lightweight summary for stages without rich contracts yet ────────

export interface GenericRunSummary {
  id: string;
  websiteId: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  warnings?: string[];
}

// ── Stage 1: Discovery ───────────────────────────────────────────────────────

export function triggerDiscovery(
  websiteId: string,
): Promise<DiscoveryRunSummary> {
  return apiPost(`/websites/${websiteId}/discovery`, {});
}

// ── Stage 2: Canonicalisation ────────────────────────────────────────────────

export function triggerCanon(
  websiteId: string,
  discoveryRunId?: string,
): Promise<GenericRunSummary> {
  return apiPost(`/websites/${websiteId}/canon`, { discoveryRunId });
}

// ── Stage 3: Classification ──────────────────────────────────────────────────

export function triggerClassification(
  websiteId: string,
  canonRunId?: string,
): Promise<GenericRunSummary> {
  return apiPost(`/websites/${websiteId}/classification`, { canonRunId });
}

// ── Stage 4: Template detection ──────────────────────────────────────────────

export function triggerTemplates(
  websiteId: string,
  opts?: {
    discoveryRunId?: string;
    canonRunId?: string;
    classificationRunId?: string;
  },
): Promise<TemplateRunSummary> {
  return apiPost(`/websites/${websiteId}/templates`, opts ?? {});
}

// ── Stage 5: Renders ─────────────────────────────────────────────────────────

export function triggerRenders(websiteId: string): Promise<GenericRunSummary> {
  return apiPost(`/websites/${websiteId}/renders`, {});
}

// ── Stage 6: Scans ───────────────────────────────────────────────────────────

export function triggerScans(websiteId: string): Promise<GenericRunSummary> {
  return apiPost(`/websites/${websiteId}/scans`, {});
}

// ── Stage 7: Normalisations ──────────────────────────────────────────────────

export function triggerNormalisations(
  websiteId: string,
): Promise<GenericRunSummary> {
  return apiPost(`/websites/${websiteId}/normalisations`, {});
}

// ── Stage 8: Screenshots ─────────────────────────────────────────────────────

export function triggerScreenshots(
  websiteId: string,
): Promise<GenericRunSummary> {
  return apiPost(`/websites/${websiteId}/screenshots`, {});
}

// ── Stage 9: Scores ──────────────────────────────────────────────────────────

export function triggerScore(
  websiteId: string,
  normRunId?: string,
): Promise<ScoreRunSummary> {
  return apiPost(`/websites/${websiteId}/scores`, { normRunId });
}

// ── Stage 10: Reports ────────────────────────────────────────────────────────

export function triggerReport(
  websiteId: string,
  opts?: {
    scoreRunId?: string;
    normRunId?: string;
    screenshotRunId?: string;
  },
): Promise<ReportRunSummary> {
  return apiPost(`/websites/${websiteId}/reports`, opts ?? {});
}

// ── Stage 11: Statements ─────────────────────────────────────────────────────

export function triggerStatement(
  websiteId: string,
  opts?: {
    reportRunId?: string;
    wcagLevel?: string;
    manualReviewStatus?: string;
  },
): Promise<StatementVersionSummary> {
  return apiPost(`/websites/${websiteId}/statements`, opts ?? {});
}

// ── Stage 12: Documents ──────────────────────────────────────────────────────

export function triggerDocuments(
  websiteId: string,
  opts?: { discoveryRunId?: string; scanPdfs?: boolean },
): Promise<DocumentRunSummary> {
  return apiPost(`/websites/${websiteId}/documents`, opts ?? {});
}

// ── Stage 13: Recurring comparison ──────────────────────────────────────────

export function triggerRecurring(
  websiteId: string,
): Promise<RecurringRunSummary> {
  return apiPost(`/websites/${websiteId}/recurring`, {});
}
